import { useSchedulerPropsContext } from '../../context/schedulerContext'
import {
  CSSProperties,
  UIEvent,
  useCallback,
  useLayoutEffect,
  useRef,
} from 'react'
import TodayTypo from '../common/TodayTypo'
import { TimeSlotGuide } from '../TimeSlotGuide/TimeSlotGuide'
import { DayColumn } from '../events/DayColumn'
import { isToday } from 'date-fns'
import styles from './MultiDaySlotLayout.module.css'
import { ProcessedEvent } from '../../types'
import { filterSingleDayEventsByDay } from '../../helpers/eventFilters.ts'
import { CurrentTimeIndicator } from '../CurrentTimeIndicator/CurrentTimeIndicator'
import { CELL_HEIGHT } from '../../helpers/constants'
import { useMinuteHeight } from '../../hooks/eventHooks'

export interface MultiDaySlotLayoutProps {
  weekDayDates: Date[]
  cellTimeSlots: Date[]
  events: ProcessedEvent[]
}

// Open the day scrolled to 07:00
const DEFAULT_SCROLL_TOP_MINUTES = 7 * 60
// Persisted across re-renders and remounts so that navigating between
// weeks keeps the user's chosen time-of-day in view.
let rememberedScrollTop: number | null = null

export function MultiDaySlotLayout({
  weekDayDates,
  cellTimeSlots,
  events,
}: MultiDaySlotLayoutProps) {
  const { locale, calendars, view } = useSchedulerPropsContext()

  const byDay = weekDayDates.map((date) => {
    return {
      date,
      isTodayCell: isToday(date),
      todayEvents: filterSingleDayEventsByDay(
        date,
        events
      ).filter((event) => calendars.has(event.calendarId)),
    }
  })

  const minuteHeight = useMinuteHeight()
  const weekBodyRef = useRef<HTMLDivElement>(null)
  const weekKey = weekDayDates[0]?.toDateString() ?? ''

  useLayoutEffect(() => {
    weekBodyRef.current?.scrollTo({
      left: 0,
      top:
        rememberedScrollTop ??
        DEFAULT_SCROLL_TOP_MINUTES * minuteHeight,
    })
  }, [weekKey, minuteHeight])

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      rememberedScrollTop = event.currentTarget.scrollTop
    },
    []
  )

  return (
    <div
      className={styles.MultiDaySlotLayout}
      style={
        {
          '--Week-days': byDay.length,
          '--Week__slotHeight': `${CELL_HEIGHT}px`,
        } as CSSProperties
      }
    >
      <div className={styles.MultiDaySlotLayout__header}>
        <div
          dangerouslySetInnerHTML={{
            __html: '<!-- empty cell -->',
          }}
        />
        {byDay.map(({ date }) => {
          return (
            <TodayTypo
              key={date.getTime()}
              date={date}
              locale={locale}
              view={view}
            />
          )
        })}
      </div>
      <div
        className={styles.MultiDaySlotLayout__body}
        ref={weekBodyRef}
        onScroll={handleScroll}
      >
        <div
          className={styles.MultiDaySlotLayout__timeSlotGuides}
        >
          {cellTimeSlots.map((cellTimeSlot) => (
            <div
              key={cellTimeSlot.getTime()}
              className={
                styles.MultiDaySlotLayout__timeSlotGuide
              }
            ></div>
          ))}
        </div>
        <div className={styles.MultiDaySlotLayout__days}>
          <div
            className={
              styles.MultiDaySlotLayout__timeSlotHeaders
            }
          >
            {cellTimeSlots.map((cellTimeSlot) => {
              return (
                <TimeSlotGuide
                  key={cellTimeSlot.getTime()}
                  time={cellTimeSlot}
                  locale={locale}
                />
              )
            })}
          </div>
          {byDay.map(({ date, todayEvents, isTodayCell }) => {
            return (
              <DayColumn
                key={date.getTime()}
                date={date}
                events={todayEvents ?? []}
                isToday={isTodayCell}
              />
            )
          })}
        </div>
        <CurrentTimeIndicator weekDayDates={weekDayDates} />
      </div>
    </div>
  )
}
